import { useTranslation } from "react-i18next";
import {
  Box,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useBottomControlsSectionCollapse } from "@rentablo/components";
import {
  CurrencyToggleMode,
  useCurrencyDisplay,
} from "@providers/CurrencyDisplayProvider";
import { useCurrencyToggleStorage } from "./useCurrencyToggleStorage";

export type { CurrencyToggleMode };

interface Props {
  storageKey: string;
  originalCurrency?: string;
  color?: "textPrimary" | "textSecondary";
  fullWidth?: boolean;
  variant?: "switch" | "buttons";
}

export const CurrencyToggle = ({
  storageKey,
  originalCurrency,
  color = "textSecondary",
  fullWidth,
  variant = "switch",
}: Props) => {
  const { t } = useTranslation();
  const { globalCurrency, isLoading } = useCurrencyDisplay();
  const { value: currentValue, onChange: handleStorageChange } =
    useCurrencyToggleStorage(storageKey);
  const collapseSection = useBottomControlsSectionCollapse();

  const handleChange = (next: CurrencyToggleMode) => {
    handleStorageChange(next);
    collapseSection();
  };

  if (isLoading) return null;

  if (
    originalCurrency &&
    originalCurrency.toUpperCase() === globalCurrency.toUpperCase()
  ) {
    return null;
  }

  if (variant === "buttons") {
    return (
      <Box
        display="flex"
        alignSelf="flex-start"
        width={fullWidth ? "100%" : "initial"}
      >
        <ToggleButtonGroup
          exclusive
          fullWidth={fullWidth}
          value={currentValue}
          onChange={(_, next) => {
            if (next) handleChange(next as CurrencyToggleMode);
          }}
        >
          <ToggleButton value="original" sx={{ whiteSpace: "nowrap" }}>
            {t("currencyToggle.original")}
          </ToggleButton>
          <ToggleButton value="global" sx={{ whiteSpace: "nowrap" }}>
            {t("currencyToggle.global", { currency: globalCurrency })}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  }

  return (
    <Box display="flex" alignSelf="center">
      <Stack direction="row" alignItems="center" spacing={3}>
        <Typography variant="body3" color={color}>
          {t("currencyToggle.original")}
        </Typography>
        <Switch
          size="small"
          checked={currentValue === "global"}
          onChange={(e) =>
            handleChange(e.target.checked ? "global" : "original")
          }
          name="currency-toggle-switch"
          color="primary"
        />
        <Typography variant="body3" color={color}>
          {globalCurrency}
        </Typography>
      </Stack>
    </Box>
  );
};
